import { YYYY_MM_DD_FORMAT } from "constants"
import { SALES_PIPELINE } from "constants/app-paths"
import dayjs from "dayjs"
import { t } from "i18next"
import getPrivilege from "utils/privileges/privileges"
import theme from "utils/themeVars"

const createItems = (itemsData, enabledWithSlugs) => {
  return itemsData.reduce(
    (acc, item) => ({
      ...acc,
      [enabledWithSlugs ? item.key : item.id]: item,
    }),
    {}
  )
}

export const calendarEventsList = () => [
  { key: "1", label: t("Client Meeting"), value: "task" },
  { key: "2", label: t("Sales Event"), value: "sale" },
  { key: "3", label: t("Management Meetings"), value: "managementMeetings" },
  { key: "4", label: t("Unavailability"), value: "unavailable" },
  { key: "5", label: t("Actions "), value: "uploadSignedBookingForm,contractListingExpiry,rqExpiry" },
]

export const classifications = (enabledWithSlugs = false) => {
  const itemsData = [
    { id: 1, name: t("Very hot"), order: 10, key: "very_hot", style: { color: "#B20E1A" } },
    { id: 2, name: t("Hot"), order: 20, key: "hot", style: { color: "#FF5F00" } },
    { id: 3, name: t("Moderate"), order: 30, key: "moderate", style: { color: "#E6B32C" } },
    { id: 4, name: t("Cold"), order: 40, key: "cold", style: { color: "#07C6D5" } },
    { id: 5, name: t("Very cold"), order: 50, key: "very_cold", style: { color: "#2259AD" } },
  ]

  return createItems(itemsData, enabledWithSlugs)
}

export const noClassification = (enabledWithSlugs = false) => {
  const itemData = [
    { id: -1, name: t("No Classification"), order: 0, key: "no_classification", style: { color: "#808080" } },
  ]

  return createItems(itemData, enabledWithSlugs)
}

export const leadSharePurposeList = () => [
  { key: 1, label: t("Payment/Documentation"), value: "payment_documentation" },
  { key: 2, label: t("Site Visit"), value: "site_visit" },
  { key: 3, label: t("Mutual Agreement"), value: "mutual_agreement" },
]

export const bookingFormStatuses = {
  draft: { title: "Draft", color: "#A7A7A7", textColor: "#FFFFFF" },
  completed: { title: "Completed", color: "#5D9C59", textColor: "#FFFFFF" },
  signed: { title: "Signed", color: "#0091BA", textColor: "#FFFFFF" },
  cancelled: { title: "Cancelled", color: "#B46060", textColor: "#FFFFFF" },
  spa_issued: { title: "SPA Issued", color: "#5D9C59", textColor: "#FFFFFF" },
  dispatched: { title: "Dispatched", color: theme["primary-light"], textColor: theme["primary-color"] },
}

export const DocumentStatusColors = {
  draft: { title: "Draft", color: "#F0F0F0", textColor: "#707070" },
  completed: { title: "Completed", color: "#E6F5F9", textColor: "#0091BA" },
  signed: { title: "Signed", color: "#EDF8F0", textColor: "#009F2B" },
  cancelled: { title: "Cancelled", color: "#FEF2F2", textColor: "#E83A3A" },
  spa_issued: { title: "SPA Issued", color: "#EDF8F0", textColor: "#009F2B" },
}

export const statusColors = {
  draft: { title: "Draft", color: "#A7A7A7", textColor: "#FFFFFF" },
  completed: { title: "Completed", color: "#5D9C59", textColor: "#FFFFFF" },
  signed: { title: "Signed", color: "#0091BA", textColor: "#FFFFFF" },
  cancelled: { title: "Cancelled", color: "#B46060", textColor: "#FFFFFF" },
  spa_issued: { title: "SPA Issued", color: "#5D9C59", textColor: "#FFFFFF" },
  pending: { title: t("Pending"), color: "#FFA500", textColor: "#FFFFFF" },
}
export const colorsAgainstUsers = ["#3E4B8B", "#908708", "#A52F00", "#004A5A"]
export const STATIC_PRIVILEGES = {
  bookingForm: { CUADOC: [], CVADOC: [], CCBF: [], CAADOC: [], CSBF: [], CPBF: [] },
  morningMeeting: {
    CAMM: [],
    CVD: [],
    CVDSF: [],
    CVDO: [],
    CVDLR: [],
    CVDQLT: [],
    CVDPT: [],
    CVDCALLR: [],
    CVDCLASSR: [],
    CVDPR: [],
    CVSD2: [],
    CANA: [],
    CAL: [],
    CARP: [],
    CVM: [],
    CCM: [],
  },
  managementMeeting: {
    CVM: [],
    CCM: [],
    CAMM: [],
  },
  staffProfile: { CVS: [], CUPS: [] },
  dashboard: { CVD: [], CVLDRS: [], CVLDRA: [] },
  saleEvent: { CVCED: [], CVCED: [], CVEC: [], CVSE: [], CSTL: [], CATOL: [], CCESA: [], CCECS: [] },
  leadsListing: { CATOL: [], CVMLEAD: [] },
}

export const reallocatedTypes = (privileges, showManualOptions) => {
  return [
    { key: 1, label: t("Auto - Lead Allocation Policy"), value: "auto_allocation" },
    ...(showManualOptions
      ? [
          {
            key: 2,
            label: t("Round Robin - Select Staff"),
            value: "round_robin",
            ...(privileges && { disabled: !getPrivilege("can_manually_allocate_external_lead", privileges) }),
          },
          {
            key: 3,
            label: t("Manual - Select Staff"),
            value: "manual",
            ...(privileges && { disabled: !getPrivilege("can_manually_allocate_external_lead", privileges) }),
          },
        ]
      : []),
  ]
}

export const billingTypes = () => ({
  1: { key: "medallion" },
  2: { key: "non_medallion" },
  3: { key: "tripartite" },
  4: { key: "escrow" },
})

export const KPIFrequencies = () => [
  { value: "monthly", label: t("Monthly") },
  { value: "weekly", label: t("Weekly") },
  { value: "daily", label: t("Daily") },
]

export const InstallmentTypes = () => [
  { key: 1, label: t("Construction Based"), value: "construction_based" },
  { key: 2, label: t("Time Based"), value: "time_based" },
]

export const poolsTableColumnInfo = () => [
  { name: t("Pool Id"), tooltip: "Pool ID", dataIndex: "poolId", className: "tableStartSpace", width: 160 },
  { name: t("Pool Name"), tooltip: "Pool Name", dataIndex: "poolName" },
  {
    name: t("Lead Status"),
    tooltip: t("Leads with current status shall be eligible for this pool IF all other criteria are met"),
    dataIndex: "leadStatus",
  },
  {
    name: t("Project"),
    tooltip: t("List of all projects whose leads shall be eligible for this pool IF all other criteria are met"),
    dataIndex: "project",
  },
  {
    name: t("Campaign"),
    tooltip: t("List of all campaigns whose leads shall be eligible for this pool IF all other criteria are met"),
    dataIndex: "campaign",
  },
  { name: t("Leads"), tooltip: t("Total Count of leads allocated on the pool"), dataIndex: "leads" },
  { name: t("Staff"), tooltip: t("Total Count of staff allocated on the pool"), dataIndex: "staff" },
  {
    name: t("Ranking"),
    tooltip: t(
      "The column represents the ranking of the pool, leads allocated to higher ranked pools shall be served first and vice versa"
    ),
    dataIndex: "ranking",
  },
  { name: t("Actions"), tooltip: t("Actions"), dataIndex: "actions" },
]

const pipeLineFilter = () => [
  { label: t("Revenue"), value: "revenue" },
  { label: t("Leads"), value: "leads" },
]

const pipelineObject = () => {
  return {
    componentType: "barChart",
    loadType: "pipeline",
    dataObject: "revenue",
    xField: "dataType",
    yField: "value",
    isStacked: true,
    withMainTitleCount: false,
    showTotalKey: false,
    tab: t("Pipeline"),
    key: "pipeline",
    tabExtraHeader: true,
    extraValueWithPercentSign: false,
    extraTitleType: "filter",
    defaultFilter: "revenue",
    unselectedLegends: ["Probable Revenue"],
    filterData: pipeLineFilter(),
    isClickable: true,
    colorScheme: "revenue",
    valueType: "currency",
  }
}

export const pipeLineAndClientFunnelTab = (updatedQueryObj = {}) => [
  updatedQueryObj?.reportType !== "affiliate" && pipelineObject(),
  {
    componentType: "funnel",
    title: t("Total Clients"),
    type: "Clients",
    loadType: "clients",
    dataObject: "clients",
    tab: t("Funnel"),
    key: "clients",
    tabExtraHeader: true,
    extraValueWithPercentSign: true,
  },
]

export const dashboardClientsAndLeadsTabs = () => [
  pipelineObject(),
  {
    componentType: "funnel",
    title: t("Total Clients"),
    type: "Clients",
    loadType: "clients",
    extraTitle: t("Total Clients"),
    subTitleValueIdentifier: "totalClients",
    tab: t("Client Funnel"),
    key: "clients",
  },
]

export const callMeetingTabsReport = () => [
  {
    componentType: "barChart",
    tab: t("Calls"),
    key: "calls",
    loadType: "calls",
    dataObject: "callData",
    xField: "callType",
    yField: "value",
    extraTitle: t("Total Qualified Calls"),
    subTitleValueIdentifier: "totalQualifiedCalls",
    tabExtraHeader: true,
  },
  {
    componentType: "barChart",
    tab: t("Meetings"),
    key: "meetings",
    loadType: "meetings",
    dataObject: "meetingData",
    xField: "meetingType",
    yField: "value",
    extraTitle: t("Meetings Done"),
    subTitleValueIdentifier: "meetingsDone",
    tabExtraHeader: true,
  },
]

export const callMeetingTabs = () => [
  {
    componentType: "barChart",
    tab: t("Calls"),
    key: "calls",
    loadType: "calls",
    dataObject: "callData",
    xField: "callType",
    yField: "value",
    extraTitle: t("Total Qualified Calls"),
    subTitleValueIdentifier: "totalQualifiedCalls",
    tabExtraHeader: true,
  },
  {
    componentType: "barChart",
    tab: t("Meetings"),
    key: "meetings",
    loadType: "meetings",
    dataObject: "meetingData",
    xField: "meetingType",
    yField: "value",
    extraTitle: t("Meetings Done"),
    subTitleValueIdentifier: "meetingsDone",
    tabExtraHeader: true,
  },
]
export const affiliatesActiveTabs = () => [
  {
    componentType: "barChart",
    tab: t("Active vs Assigned Affiliates"),
    key: "assigned_active_affs",
    loadType: "assigned_active_affs",
    dataObject: "affiliates",
    xField: "dataType",
    yField: "value",
    isStacked: false,
  },
]

export const productivityRevenueTabs = () => [
  {
    componentType: "barChart",
    tab: t("Productivity"),
    key: "productivity",
    loadType: "productivity",
    dataObject: "productivityByMonth",
    xField: "month",
    yField: "productivity",
    extraTitle: t("4 Month Average"),
    subTitleValueIdentifier: "totalAverageProductivity",
    withMainTitleCount: false,
    extraValueWithPercentSign: true,
  },
  {
    componentType: "barChart",
    tab: t("Revenue"),
    key: "revenue",
    loadType: "revenue",
    dataObject: "revenue",
    xField: "dataType",
    yField: "value",
    isStacked: false,
  },
]

export const latestaffiliateSalesAndLeads = () => [
  {
    componentType: "barChart",
    tab: t("New Affiliates"),
    key: "new_affs",
    loadType: "new_affs",
    dataObject: "new_affs_data",
    xField: "weekInterval",
    yField: "count",
  },
  {
    componentType: "barChart",
    tab: t("New Leads"),
    key: "new_leads",
    loadType: "new_leads",
    dataObject: "new_leads_data",
    xField: "weekInterval",
    yField: "count",
  },
]



/** KPI Management: Meetings chart – Breakdown (donut) + Trend (line) */
export const meetingAttendanceTabs = () => [
  {
    componentType: "donutChart",
    tab: t("Breakdown"),
    key: "breakdown",
    loadType: "meeting_attendance",
    dataObject: "meeting_breakdown",
    angleField: "value",
    colorField: "type",
    colorScheme: "meetingAttendance",
    radius: 0.8,
    innerRadius: 0.7,
    tabExtraHeader: true,
    headerTitleValueOnRight: true,
    subTitleValueIdentifier: "totalMeetingsAttended",
  },
  {
    componentType: "lineChart",
    tab: t("Trend"),
    key: "trend",
    loadType: "meeting_attendance_trend",
    dataObject: "meeting_trend",
    xField: "date",
    yField: "value",
    seriesField: "type",
    tabExtraHeader: false,
    headerTitleValueOnRight: true,
    // extraTitle: t("Meetings"),
    subTitleValueIdentifier: "total",
  },
]

/** KPI Management: AI Meeting Scoring – Breakdown (progress bars) + Trend (line) */
export const meetingScoringTabs = () => [
  {
    componentType: "scoringBreakdown",
    tab: t("Breakdown"),
    key: "breakdown",
    loadType: "ai_meeting_score_breakdown",
    dataObject: "scoring_breakdown",
    extraTitleType: "filter",
    defaultFilter: "morning_meeting",
    filterData: [
      { label: t("All Meetings"), value: "all" },
      { label: t("Morning Meeting"), value: "morning_meeting" },
      { label: t("One on One"), value: "one_on_one" },
      { label: t("Allied Meeting"), value: "allied" },
      { label: t("New Hire Progression"), value: "new_hire" },
    ],
    progressProps: {
      showInfo: false,
      trailColor: "#F2F2F2",
      strokeLinecap: "round",
    },
  },
  {
    componentType: "lineChart",
    tab: t("Trend"),
    key: "trend",
    loadType: "ai_meeting_score_trend",
    dataObject: "scoring_trend",
    xField: "date",
    yField: "value",
    seriesField: "type",
    lineColorScheme: "scoringTrend",
    tabExtraHeader: false,
    headerTitleValueOnRight: true,
    extraTitle: t(""),
    subTitleValueIdentifier: "avgScore",
  },
]

/** KPI Management: Reviews chart – Lead, Compliance Audit, Client Observation (all donut) + Received/Done filter */
export const reviewsTabs = () => [
  {
    componentType: "donutChart",
    tab: t("Lead Reviews"),
    key: "lead_reviews",
    loadType: "lead_review",
    dataObject: "donutData",
    angleField: "value",
    colorField: "type",
    colorScheme: "reviewsRatings",
    radius: 0.8,
    innerRadius: 0.7,
  },
  {
    componentType: "donutChart",
    tab: t("Compliance Audit"),
    key: "audit_reviews",
    loadType: "audit_review",
    dataObject: "donutData",
    angleField: "value",
    colorField: "type",
    colorScheme: "auditReviews",
    radius: 0.8,
    innerRadius: 0.7,
  },
  {
    componentType: "donutChart",
    tab: t("Client Observation"),
    key: "client_observation",
    loadType: "client_observations",
    dataObject: "donutData",
    angleField: "value",
    colorField: "type",
    colorScheme: "reviewsRatings",
    radius: 0.8,
    innerRadius: 0.7,
  },
]

/** Color schemes for TabBasedMultiCharts – pass as colorSchemes prop to override chart colors */
export const tabBasedMultiChartsColorSchemes = {
  meetingAttendanceLine: {
    "As Host": "#E53935",
    "As Participant": "#1E88E5",
  },
  meetingAttendanceDonut: {
    Morning: "#337AD8",
    "1 on 1": "#74ABF5",
    Allied: "#ABCFFE",
    "New Hire": "#D6E8FF",
  },
  scoringTrendLine: {
    Overall: "#1F2937",
    Morning: "#337AD8",
    "1 on 1": "#74ABF5",
    Allied: "#ABCFFE",
    "New Hire": "#D6E8FF",
  },
  getScoreProgressColor: percent => {
    if (percent >= 70) return "#99D6B3"
    if (percent >= 50) return "#EBD482"
    return "#F49E9E"
  },
  reviewsRatingsDonut: {
    Excellent: "#E85D5D",
    "Good": "#E67A7A",
    Satisfactory: "#DB9797",
    "Needs Improvement": "#E6BDBD",
    Unsatisfactory: "#EED9D9",
  },
}

export const individualUserAgendaTabs = () => [
  {
    componentType: "listItems",
    loadType: "todos_for_today",
    tab: t("Today"),
    key: "todos_for_today",
  },
  {
    componentType: "listItems",
    loadType: "todos_overdue",
    tab: t("Overdue"),
    key: "todos_overdue",
  },
  {
    componentType: "listItems",
    loadType: "todos_upcoming",
    tab: t("Upcoming"),
    key: "todos_upcoming",
  },
]

export const pUTargetsAndPerformanceTabs = () => [
  {
    componentType: "table",
    loadType: "pu_targets",
    tab: t("PU Targets"),
    key: "pu_targets",
  },
  {
    componentType: "table",
    loadType: "staff_performance",
    tab: t("Staff Performance"),
    key: "staff_performance",
  },
]
export const insightsAndLogsTabs = isLineManager => [
  // TODO: Backend not implemented
  // {
  //   componentType: "aiInsights",
  //   loadType: "ai_insights",
  //   tab: t("AI Insights"),
  //   key: "ai_insights",
  // },
  {
    componentType: "table",
    loadType: "activity_logs",
    tab: t("Activity Logs"),
    key: "activity_logs",
  },
  isLineManager && {
    componentType: "table",
    loadType: "todos",
    tab: t("Todos"),
    key: "todos",
  },
]

export const defaultActiveTabForTargvsAch = () => ({
  componentType: "radialBar",
  type: "Targets",
  loadType: "target_vs_achieved",
  targetCategory: "calls",
  dataObject: "target",
  tab: t("Calls"),
  key: "calls",
  filterData: [
    { label: t("Date Range"), value: "CompletionPercentage" },
    { label: t("Month"), value: "CompletionMonthlyPercentage" },
  ],
})

export const performingAndInterestedProjectsTabs = () => [
  {
    componentType: "table",
    loadType: "top_performing_projects",
    tab: t("Top Performing Projects"),
    key: "top_performing_projects",
  },
  {
    componentType: "table",
    loadType: "most_interested_projects",
    tab: t("Most Interested Projects"),
    key: "most_interested_projects",
  },
]

export const performingAffiliates = () => [
  {
    componentType: "table",
    loadType: "top_performing_affs_by_sales",
    tab: t("Affilites Ranking By Sales"),
    key: "top_performing_affs_by_sales",
  },
  {
    componentType: "table",
    loadType: "top_performing_affs_by_leads",
    tab: t("Affilites Ranking By Leads "),
    key: "top_performing_affs_by_leads",
  },
]

export const onPipelineBarClickLink = (getAppliedFilters, userId) => {
  return `${SALES_PIPELINE}?allocatedTo=${getAppliedFilters?.userIds ? getAppliedFilters?.userIds : userId}&month=${
    getAppliedFilters?.toDate
      ? dayjs(decodeURIComponent(getAppliedFilters?.toDate)).format(YYYY_MM_DD_FORMAT)
      : dayjs().format(YYYY_MM_DD_FORMAT)
  }`
}

export const flagColorMap = {
  critical_follow_up_decreasing_prob: { flagColor: "#E83A3A", icon: "AiFillWarning", bgColor: "#fef7f7" },
  suspicious_sale_low_engagement: { flagColor: "#E83A3A", icon: "AiFillWarning", bgColor: "#fef7f7" },
  suspicious_sale_payment_rejection: { flagColor: "#E83A3A", icon: "AiFillWarning", bgColor: "#fef7f7" },
  suspicious_lead_contract_cancelled: { flagColor: "#E83A3A", icon: "AiFillWarning", bgColor: "#fef7f7" },
  flagged_in_inquiry_feedback: { flagColor: "#E83A3A", icon: "AiFillWarning", bgColor: "#fef7f7" },
  audit_concerns: { flagColor: "#E83A3A", icon: "IoFlag", bgColor: "#fef7f7" },
  post_token_follow_up: { flagColor: "#D6A700", icon: "GiTwoCoins", bgColor: "#FBF6E5" },
  post_meeting_follow_up: { flagColor: "#D6A700", icon: "FaHandshakeSimple", bgColor: "#FBF6E5" },
  upcoming_meetings: { flagColor: "#0091BA", icon: "FaCalendar", bgColor: "#FAFDFE" },
  event_follow_up: { flagColor: "#D6A700", icon: "FaUsers", bgColor: "#FBF6E5" },
  new_shifted_leads: { flagColor: "#0091BA", icon: "HiOutlineRefresh", bgColor: "#FAFDFE" },
  high_conversion: { flagColor: "#009F2B", icon: "MdTrendingUp", bgColor: "#EDF8F0" },
  stalling_conversion: { flagColor: "#E83A3A", icon: "AiFillWarning", bgColor: "#fef7f7" },
  declining_conversion: { flagColor: "#E83A3A", icon: "MdTrendingDown", bgColor: "#fef7f7" },
  expiring_token: { flagColor: "#E83A3A", icon: "AiFillWarning", bgColor: "#fef7f7" },
  expiring_pdp: { flagColor: "#E83A3A", icon: "AiFillWarning", bgColor: "#fef7f7" },
  contracts_expiring: { flagColor: "#E83A3A", icon: "AiFillWarning", bgColor: "#fef7f7" },
}
export const workFlowTypeOptions = () => [
  { label: t("Custom"), value: "custom" },
  { label: t("Pre-defined"), value: "predefined" },
]

export const projectClassificationLabels = () => {
  return {
    "A+": theme["primary-color"],
    A: "#0091ba",
    B: "#e8bb44",
    C: "#fe711d",
  }
}
export const payoutCommissionTiers = () => [
  { label: t("Tier 1"), value: "tier_1" },
  { label: t("Tier 2"), value: "tier_2" },
  { label: t("Tier 3"), value: "tier_3" },
  { label: t("Tier 4"), value: "tier_4" },
]

export const payoutStatusColors = () => ({
  rejected: { title: t("Rejected"), color: "#fef2f2", textColor: "#e83a3a" },
  in_progress: { title: t("In Progress"), color: "#e6f5f9", textColor: "#0091ba" },
  draft: { title: t("Draft"), color: "#f6f0dc", textColor: "#d6a700" },
  completed: { title: t("Completed"), color: "#edf8f0", textColor: "#009f2b" },
})

export const booleanOptionsforClientsAndLeads = () => {
  return [
    { label: t("Yes"), value: "true" },
    { label: t("No"), value: "false" },
  ]
}

export const ratingColorMap = ()=> ({
  "Unsatisfactory": theme["error-color"],
  "Needs Improvement": theme["average-color"],
  Satisfactory: theme["warning-color"],
  Good: theme["primary-color"],
  Excellent: "#007c21",
})

export const contractTextType = {
  complete_down_payment: "success",
  sold: "success",
  token: "secondaryLight",
  partial_down_payment: "info",
}

export const categoryItem = {
  "A+": { color: "#FCFAE6", textColor: "#C68700", icon: "HiTrophy" },
  A: { color: "#FCFAE6", textColor: "#C68700", icon: "MdStars" },
  B: { color: "#F5F5F5", textColor: "#424242", icon: "MdStars" },
  C: { color: "#FDF5EA", textColor: "#742E1E", icon: "MdStars" },
}
